import { FormControlLabel, Stack } from '@mui/material';
import { forwardRef } from 'react';
import { VyTypography } from '../../data-display/VyTypography';
import { VySwitch, type VySwitchProps } from '../VySwitch';

/**
 * @tier molecule
 *
 * Labeled switch row: a `VySwitch` atom with an opinionated title +
 * description label and an optional helper/validation line. Layout is
 * fixed — title + description stack on the left, switch on the right
 * (matches Figma). This is the form-field shape consumed by drawers and
 * by the `VyToggleSection` organism.
 *
 * The label is **not** a ReactNode slot — pass `title` (string) and
 * optionally `description` (string). All remaining props pass through to
 * the underlying `VySwitch` atom (`checked`, `onChange`, `disabled`, …).
 */
export type VySwitchRowProps = VySwitchProps & {
  /** Title shown next to the switch. Always required. */
  title: string;
  /** Optional muted description line under the title. */
  description?: string;
  /** Renders `helperText` in the error color. */
  error?: boolean;
  /** Extra line under the description — typically a validation message when `error` is true. */
  helperText?: string;
  /** Default: 'start' — title on the left, switch on the right (matches Figma). */
  labelPlacement?: 'start' | 'end';
  /** Layout span hint forwarded to the root, consumed by grid containers (e.g. FieldGrid). */
  'data-field-span'?: string;
};

export const VySwitchRow = forwardRef<HTMLButtonElement, VySwitchRowProps>(
  function VySwitchRow(
    {
      title,
      description,
      error,
      helperText,
      labelPlacement = 'start',
      'data-field-span': dataFieldSpan,
      ...switchProps
    },
    ref,
  ) {
    return (
      <FormControlLabel
        // Layout hint (e.g. modify-panel FieldGrid) belongs on the root, not the
        // inner input, so grid containers can match it as a direct child.
        data-field-span={dataFieldSpan}
        control={<VySwitch ref={ref} {...switchProps} />}
        labelPlacement={labelPlacement}
        sx={{
          marginInline: 0,
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 5,
        }}
        label={
          <Stack sx={{ gap: 0.25 }}>
            <VyTypography variant="switchTitle" sx={{ color: 'text.primary' }}>
              {title}
            </VyTypography>
            {description && <VyTypography variant="fieldHelper">{description}</VyTypography>}
            {helperText && (
              <VyTypography
                variant="fieldHelper"
                sx={{ color: error ? 'error.main' : 'text.secondary' }}
              >
                {helperText}
              </VyTypography>
            )}
          </Stack>
        }
      />
    );
  },
);
